import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal';
import './typography.scss';
import Loading from '../../components/Loading';
import api from '../../services/api';

export interface CadernoLPMAHistoricoItem {
	data: string;
	autor: string;
	texto: string;
}

interface CadernoLPMAHistoricoModalProps {
	show: boolean;
	onHide: () => void;
	cadernoLPMAId: number | null;
}

const NOMES_CAMPOS: Record<string, string> = {
	Observacao: 'Observação',
	ProcessoCIBIO: 'Processo CIBIO',
	DataRecebimento: 'Data recebimento',
	DataPlantio: 'Data plantio',
	Responsavel: 'Responsável',
	DataInicioCultivo: 'Data início cultivo',
	DataTerminoCultivo: 'Data término cultivo',
	TalhaoCultivo: 'Talhão cultivo',
	ResponsavelCultivo: 'Responsável cultivo',
	ObservacoesCultivo: 'Observações cultivo',
	DataInicioPlantio: 'Data início plantio',
	DataTerminoPlantio: 'Data término plantio',
	TalhaoPlantio: 'Talhão plantio',
	ObservacoesPlantio: 'Observações plantio',
	DataInicioColheita: 'Data início colheita',
	DataTerminoColheita: 'Data término colheita',
	ResponsavelColheita: 'Responsável colheita',
	ObservacoesColheita: 'Observações colheita'
};

function formatarTextoHistorico(texto: string): { titulo: string; linhas: string[] } {
	if (!texto || !texto.trim()) return { titulo: '', linhas: [] };

	if (texto.startsWith('O formulário foi criado:')) {
		const resumo = texto.replace('O formulário foi criado:', '').trim();
		return { titulo: 'Caderno criado', linhas: [resumo] };
	}

	if (texto.startsWith('Movimentação do caderno:')) {
		const resto = texto.replace('Movimentação do caderno:', '').trim();
		const linhas: string[] = [];
		const partes = resto.split(/\.\s+/).filter(Boolean);

		for (const p of partes) {
			const matchModulo = p.match(/^Módulo (.+?): (.+)$/);
			if (matchModulo) {
				const nomeModulo = matchModulo[1];
				const conteudo = matchModulo[2];

				const countMatch = conteudo.match(/(\d+) registro\(s\) \(era (\d+)\)/);
				if (countMatch) {
					const n = parseInt(countMatch[1], 10);
					const m = parseInt(countMatch[2], 10);
					if (n > m) linhas.push(`${nomeModulo}: +${n - m} registro(s) adicionado(s)`);
					else if (n < m) linhas.push(`${nomeModulo}: ${m - n} registro(s) removido(s)`);
					else linhas.push(`${nomeModulo}: ${n} registro(s)`);
					continue;
				}

				const regMatch = conteudo.match(/^registro (\d+): (.+)$/);
				if (regMatch) {
					const numReg = regMatch[1];
					const campos = regMatch[2];
					const alteracoes = campos.match(/'([^']+)': '([^']*)' → '([^']*)'/g) || [];
					alteracoes.forEach((alt: string) => {
						const [, nome, antigo, novo] = alt.match(/'([^']+)': '([^']*)' → '([^']*)'/) || [];
						const label = (nome && NOMES_CAMPOS[nome]) || nome || '';
						const a = antigo === '' ? '(vazio)' : antigo;
						const b = novo === '' ? '(vazio)' : novo;
						linhas.push(`${nomeModulo} (registro ${numReg}): ${label} — ${a} → ${b}`);
					});
					continue;
				}
			}
			linhas.push(p);
		}
		return { titulo: 'Alterações', linhas: linhas.length ? linhas : [resto] };
	}

	return { titulo: '', linhas: [texto] };
}

function nomeCurto(email: string): string {
	if (!email) return '';
	const idx = email.indexOf('@');
	return idx > 0 ? email.substring(0, idx) : email;
}

export default function CadernoLPMAHistoricoModal({
	show,
	onHide,
	cadernoLPMAId
}: CadernoLPMAHistoricoModalProps) {
	const [items, setItems] = useState<CadernoLPMAHistoricoItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!show || cadernoLPMAId == null) {
			setItems([]);
			setError(null);
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError(null);
		api
			.get<CadernoLPMAHistoricoItem[]>(
				`api/v1/cadernoLPMA/CadernoLPMA/historico/${cadernoLPMAId}`
			)
			.then(({ data }) => {
				if (!cancelled) {
					setItems(Array.isArray(data) ? data : []);
				}
			})
			.catch((err: any) => {
				if (!cancelled) {
					setError(err?.response?.data?.message || 'Erro ao carregar histórico.');
					setItems([]);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [show, cadernoLPMAId]);

	return (
		<Modal show={show} onHide={onHide} title="Histórico" size="lg" bodyClassName="caderno-lpma-modal-body">
			{loading && (
				<div className="d-flex justify-content-center py-4">
					<Loading />
				</div>
			)}
			{!loading && error && (
				<div className="alert alert-danger" role="alert">
					{error}
				</div>
			)}
			{!loading && !error && items.length === 0 && (
				<p className="text-muted mb-0">Nenhum registro de histórico encontrado.</p>
			)}
			{!loading && !error && items.length > 0 && (
				<div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
					{items.map((item, index) => {
						const { titulo, linhas } = formatarTextoHistorico(item.texto);
						return (
							<div
								key={`${item.data}-${item.autor}-${index}`}
								style={{
									borderLeft: '4px solid #0d6efd',
									backgroundColor: '#f8f9fa',
									borderRadius: '6px',
									padding: '12px 14px',
									marginBottom: '12px'
								}}
							>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginBottom: '8px',
										flexWrap: 'wrap',
										gap: '4px'
									}}
								>
									<span style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>
										{item.data}
									</span>
									<span
										style={{ fontSize: '12px', color: '#6c757d' }}
										title={item.autor}
									>
										{nomeCurto(item.autor)}
									</span>
								</div>
								<div style={{ fontSize: '14px', lineHeight: 1.5, color: '#212529' }}>
									{titulo && (
										<div style={{ fontWeight: 600, marginBottom: '6px' }}>{titulo}</div>
									)}
									{linhas.length === 1 && !titulo ? (
										<div>{linhas[0]}</div>
									) : (
										<ul style={{ margin: 0, paddingLeft: '20px' }}>
											{linhas.map((linha, i) => (
												<li key={i} style={{ marginBottom: '4px' }}>
													{linha}
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</Modal>
	);
}
